import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useRouteError } from 'react-router-dom';
import type { RouteObject, createMemoryRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import {
    ConvertDjangoNotifications,
    NotificationsContainer,
    NotificationsProvider,
} from '../notifications/Notifications';
import { NotFoundPage } from './NotFoundPage';
import { ErrorPageFallback } from './error-boundary-utils';
import { MeasuringPointRoutes, ProjectRoutes } from './measuringPoint/Routes';
import { OverviewPage } from './overview/OverviewPage';
import { CustomAlarmPageRoutes } from './customAlarms/CustomAlarmsPage';
import SharingRoutes from './sharing/SharingRoutes';
import DownloadsPage from './downloads/DownloadsPage';
import NotificationsPage from './notifications/NotificationsPage';
import SoundClipsPage from './soundClips/SoundClipsPage';
import { ContactsPage } from './addressBook/ContactsPage';
import { ErrorPage } from './ErrorPage';
import type { LogArgument } from '../../utils/logger';
import profile, { Profile, ProfileProvider } from '../../profile';
import { Website } from '../website/Website';
import { ReportBuilderRoutes } from './reportBuilder/Routes';
import { PartialConfigRoutes } from './partialConfigs/Routes';
import { AttributesRoutes } from './attributes/Routes';
import { MapRoutes } from './map/Routes';
import { SearchParamsProvider } from '../../hooks/useUpdateSearchParams';
import { ChangeLanguageNextUrlUpdater } from './ChangeLanguageNextUrlUpdater';

// Used when `react-router-dom` detects an error.
function ReactRouterErrorBoundary() {
    const error = useRouteError();
    return <ErrorPage error={error as LogArgument} />;
}

interface OptionalWebsiteWrapperProps {
    wrapWithWebsite: boolean;
    children: ReactNode;
}

function OptionalWebsiteWrapper({ wrapWithWebsite, children }: OptionalWebsiteWrapperProps) {
    if (wrapWithWebsite) {
        return <Website>{children}</Website>;
    }

    return children;
}
OptionalWebsiteWrapper.propTypes = {
    wrapWithWebsite: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
};

type BrowserRouterProps = NonNullable<Parameters<typeof createBrowserRouter>[1]>;
type MemoryRouterProps = NonNullable<Parameters<typeof createMemoryRouter>[1]>;
type RouterProps = BrowserRouterProps & MemoryRouterProps;

type CreateRouterMethod = (
    routes: RouteObject[],
    opts?: RouterProps
) => ReturnType<typeof createBrowserRouter>;

interface PageContainerProps {
    createBrowserRouterMethod?: CreateRouterMethod;
    routerProps?: RouterProps;
    extraRoutes?: RouteObject[];
    routerInjectables?: ReactNode[];
    profile?: Profile;
    wrapWithWebsite?: boolean;
}

function PageContainer({
    createBrowserRouterMethod = createBrowserRouter,
    routerProps,
    extraRoutes = [],
    routerInjectables = [],
    profile: profileOverride,
    wrapWithWebsite = false,
}: PageContainerProps) {
    const router = useMemo(
        () =>
            createBrowserRouterMethod(
                [
                    {
                        errorElement: <ReactRouterErrorBoundary />,
                        element: (
                            <OptionalWebsiteWrapper wrapWithWebsite={wrapWithWebsite}>
                                <ErrorBoundary FallbackComponent={ErrorPageFallback}>
                                    <SearchParamsProvider>
                                        <ChangeLanguageNextUrlUpdater />
                                        {routerInjectables.map((injectable) => injectable)}
                                        <Outlet />
                                    </SearchParamsProvider>
                                </ErrorBoundary>
                            </OptionalWebsiteWrapper>
                        ),
                        children: [
                            { path: '/:entity/attributes/*', element: <AttributesRoutes /> },
                            { path: '/overview', element: <OverviewPage /> },
                            { path: '/measuring_point/*', element: <MeasuringPointRoutes /> },
                            { path: '/project/*', element: <ProjectRoutes /> },
                            { path: '/alarm_level/*', element: <CustomAlarmPageRoutes /> },
                            { path: '/sharing/*', element: <SharingRoutes /> },
                            { path: '/reports/downloads', element: <DownloadsPage /> },
                            { path: '/notifications', element: <NotificationsPage /> },
                            { path: '/partial_config/*', element: <PartialConfigRoutes /> },
                            { path: '/sound_clips/', element: <SoundClipsPage /> },
                            {
                                path: '/reports/builder/*',
                                element: <ReportBuilderRoutes />,
                            },
                            { path: '/map/*', element: <MapRoutes /> },
                            { path: '/address_book', element: <ContactsPage /> },
                            ...extraRoutes.map((route) => route),
                            { path: '*', element: <NotFoundPage /> },
                        ],
                    },
                ],
                routerProps
            ),
        [createBrowserRouterMethod, extraRoutes, routerProps, routerInjectables, wrapWithWebsite]
    );

    return (
        <ProfileProvider profile={profileOverride || profile}>
            <NotificationsProvider>
                <ConvertDjangoNotifications />
                <div className="main-container-new">
                    <NotificationsContainer />
                </div>

                <RouterProvider router={router} />
            </NotificationsProvider>
        </ProfileProvider>
    );
}
PageContainer.propTypes = {
    createBrowserRouterMethod: PropTypes.func,
    routerProps: PropTypes.object,
    extraRoutes: PropTypes.arrayOf(PropTypes.object),
    routerInjectables: PropTypes.arrayOf(PropTypes.object),
    profile: PropTypes.instanceOf(Profile),
    wrapWithWebsite: PropTypes.bool,
};

export default PageContainer;
